import { Gift, User, FileText } from 'lucide-react';

export function FOCSection({ data }) {
  // Create default FOC data structure
  const defaultFOCData = {
    count: 0,
    amount: 0,
    orders: []
  };

  // Handle different data structures that might come from the API
  const processFOCData = (inputData) => {
    // If no data provided, return default
    if (!inputData) return defaultFOCData;

    // Case 1: If data is already in the expected format (with orders array)
    if (inputData.orders && Array.isArray(inputData.orders)) {
      return {
        count: inputData.count || inputData.orders.length,
        amount: inputData.amount || inputData.orders.reduce((sum, order) => sum + (order.amount || 0), 0),
        orders: inputData.orders
      };
    }

    // Case 2: If data is an array (from Table8 or Table3 filtered for FOC)
    if (Array.isArray(inputData)) {
      // Filter for FOC related items
      const focItems = inputData.filter(item => 
        item.PaymentMode?.toLowerCase() === 'foc' || 
        item.mode?.toLowerCase() === 'foc' ||
        item.OrderSource?.toLowerCase().includes('foc') ||
        item.source?.toLowerCase().includes('foc')
      );

      if (focItems.length > 0) {
        return {
          count: focItems.reduce((sum, item) => sum + (item.count || item.OrderCount || 0), 0),
          amount: focItems.reduce((sum, item) => sum + (item.amount || item.TotalAmount || item.NetSales || 0), 0),
          orders: focItems.map(item => ({
            orderId: item.orderId || item.OrderMasterId || `FOC-${Math.random().toString(36).substr(2, 6)}`,
            customer: item.customer || item.CustomerName || 'Walk-in Customer',
            reference: item.reference || item.OrderSource || 'FOC',
            amount: item.amount || item.TotalAmount || item.NetSales || 0,
            items: item.items || `${item.Product || 'FOC Items'}`
          }))
        };
      }
    }

    // Case 3: If data is a summary object (from Table)
    if (inputData.FocAmount !== undefined || inputData.focAmount !== undefined) {
      const focAmount = inputData.FocAmount || inputData.focAmount || 0;
      return {
        count: focAmount > 0 ? 1 : 0, // Estimate 1 order if there's FOC amount
        amount: focAmount,
        orders: focAmount > 0 ? [{
          orderId: 'FOC-SUMMARY',
          customer: 'Summary Data',
          reference: 'FOC',
          amount: focAmount,
          items: 'FOC Items'
        }] : []
      };
    }

    // Return default if no match
    return defaultFOCData;
  };

  const processedData = processFOCData(data);
  const { count, amount, orders } = processedData;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border border-slate-200 hover:shadow-xl transition-all">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-slate-900">FOC Orders</h3>
          <p className="text-sm text-slate-500 mt-1">Free of charge details</p>
        </div>
        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 p-3 rounded-xl shadow-lg">
          <Gift className="w-5 h-5 text-white" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="p-3 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl">
          <p className="text-xs text-slate-600 mb-1">Total FOC</p>
          <p className="text-lg font-bold text-indigo-700">
            Rs {(amount || 0).toLocaleString(undefined, { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
          </p>
        </div>
        <div className="p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
          <p className="text-xs text-slate-600 mb-1">FOC Orders</p>
          <p className="text-lg font-bold text-purple-700">{count || 0}</p>
        </div>
      </div>

      <div className="space-y-3 max-h-[280px] overflow-y-auto pr-2 custom-scrollbar">
        {!orders || orders.length === 0 ? (
          <div className="text-center py-8">
            <Gift className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">No FOC orders</p>
          </div>
        ) : (
          orders.map((item, index) => (
            <div key={index} className="p-3 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-2">
                <span className="text-xs font-bold text-[#01796d]">{item.orderId || `FOC-${index + 1}`}</span>
                <span className="text-sm font-bold text-slate-900">
                  Rs {(item.amount || 0).toLocaleString(undefined, { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })}
                </span>
              </div>
              <div className="flex items-center gap-1 mb-1">
                <User className="w-3 h-3 text-slate-500" />
                <p className="text-xs text-slate-700 font-medium truncate">
                  {item.customer || item.CustomerName || 'Walk-in Customer'}
                </p>
              </div>
              <div className="flex items-center gap-1 mb-1">
                <FileText className="w-3 h-3 text-slate-500" />
                <p className="text-xs text-slate-500">{item.reference || item.OrderSource || 'FOC'}</p>
              </div>
              <p className="text-xs text-slate-400 mt-2 truncate">
                {item.items || item.Product || `${item.quantity || 1} x FOC Item${item.quantity > 1 ? 's' : ''}`}
              </p>
            </div>
          ))
        )}
      </div>

      {/* Add custom scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
}